import { Box, CircularProgress, Dialog, DialogContent } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { bulkUpdateRecordPaymentApi, multipleRecordPaymentListApi } from '../../../redux/Invoice/action';
import { saveFileFromResponse } from '../../../utility/helpers/fileDownload';
import useRecordPaymentStore from '../../../store/UseRecordPaymentStore';
import RecordPaymentListFilters from './RecordPaymentListComponents/RecordPaymentListFilters';
import RecordPaymentListHeader from './RecordPaymentListComponents/RecordPaymentListHeader';
import RecordPaymentListTable from './RecordPaymentListComponents/RecordPaymentListTable';
import RecordPaymentStickyBottom from './RecordPaymentListComponents/RecordPaymentStickyBottom';
import RecordPaymentSummaryModal from './RecordPaymentListComponents/RecordPaymentSummaryModal';

const RecordPaymentList = () => {
    const dispatch = useDispatch();
    const {
        recordPaymentParams,
        recordPaymentList,
        modifiedRecordPaymentList,
        recordPaymentPagaintion,
        selectedRows,
        showLoader,
        customerDetail,
        selectedRecordsCache, // NEW: Access to cache
        setRecordPaymentParams,
        setRecordPaymentList,
        setRecordPaymentPagaintion,
        setSelectedRows,
        setShowLoader,
        updatePaymentInModifiedList,
        addPaymentToModifiedList,
        deletePaymentFromModifiedList,
        discardChanges,
        getSavePayload,
        getTotalAdditionalPayment,
        setCustomerDetail,
        resetRecordPayment,
        getAllSelectedRecords, // NEW: Helper to get all selected records
    } = useRecordPaymentStore();

    console.log('🚀 ~ RecordPaymentList ~ selectedRows:', selectedRows);
    console.log('🚀 ~ RecordPaymentList ~ selectedRecordsCache:', selectedRecordsCache);

    const [statementAccountModal, setStatementAccountModal] = useState(false);
    const onlyOneCustomerSelected = recordPaymentParams?.customer?.length === 1 && recordPaymentParams?.company?.length === 0;
    const [loadingMore, setLoadingMore] = useState(false);

    // Main fetch - Now preserves selected records' modifications
    const getMultipleRecordPayments = useCallback(
        async (page = 1, append = false) => {
            try {
                if (page === 1) setShowLoader(true);
                else setLoadingMore(true);

                const resp = await dispatch(
                    multipleRecordPaymentListApi({
                        ...recordPaymentParams,
                        page,
                    })
                );

                if (resp?.data) {
                    if (append) {
                        // For append, we need to handle both lists
                        const currentOriginalList = recordPaymentList;
                        const newOriginalList = [...currentOriginalList, ...resp.data];
                        setRecordPaymentList(newOriginalList);
                    } else {
                        setRecordPaymentList(resp.data);
                    }

                    setRecordPaymentPagaintion({
                        totalCount: resp.totalCount ?? 0,
                        totalPages: resp.totalPages ?? 0,
                        currentPage: page,
                    });
                } else {
                    if (!append) setRecordPaymentList([]);
                    setRecordPaymentPagaintion({
                        totalCount: 0,
                        totalPages: 0,
                        currentPage: 1,
                    });
                }
            } catch (error) {
                console.error('Error fetching multiple record payments:', error);
            } finally {
                setShowLoader(false);
                setLoadingMore(false);
            }
        },
        [dispatch, recordPaymentParams, setShowLoader, setRecordPaymentList, setRecordPaymentPagaintion]
    );

    // Handle discard changes
    const handleDiscardChanges = useCallback(() => {
        discardChanges();
        setSelectedRows([]);
    }, [discardChanges, setSelectedRows]);

    // Handle save
    const handleSave = useCallback(
        async (exportButton = false) => {
            try {
                if (useRecordPaymentStore.getState().selectedRows.length === 0) {
                    toast.error('Invoices must contain at least 1 items');
                    return;
                }
                setShowLoader(true);

                const { isValid, payload } = getSavePayload();

                if (!isValid) {
                    setShowLoader(false);
                    return; // stop execution if validation failed
                }

                console.log('Save payload:', payload);
                const resp = await dispatch(bulkUpdateRecordPaymentApi({ invoices: payload, isExport: exportButton }));
                if (resp) {
                    // After successful save, clear the cache and refresh
                    getMultipleRecordPayments(1, false);
                    setSelectedRows([]);
                    // payments are saved even when the PDF fails; that message comes from the response interceptor
                    if (exportButton && resp.status === 200 && saveFileFromResponse(resp, 'payment-summary.pdf')) {
                        toast.success('Payment summary exported successfully');
                        return 'File download initiated.';
                    }
                } else {
                    setShowLoader(false);
                }
            } catch (error) {
                console.error(error);
                setShowLoader(false);
            }
        },
        [dispatch, getMultipleRecordPayments, setSelectedRows, setShowLoader, getSavePayload]
    );

    // Handle save and export
    const onSingleExport = useCallback(() => {
        if (useRecordPaymentStore.getState().selectedRows.length === 0) {
            toast.error('Invoices must contain at least 1 items');
            return;
        }
        const { isValid, payload } = getSavePayload();
        console.log('🚀 ~ RecordPaymentList ~ isValid:', isValid);
        if (!isValid) {
            setShowLoader(false);
            return; // stop execution if validation failed
        }
        setStatementAccountModal(true);
    }, [getSavePayload]);

    // Initial load and cleanup
    useEffect(() => {
        getMultipleRecordPayments(1, false);
    }, [getMultipleRecordPayments, resetRecordPayment]);

    useEffect(() => {
        return () => {
            // Reset Zustand store when unmounting (leaving page)
            resetRecordPayment();
        };
    }, []);

    // NEW: Calculate counts for better UX
    const totalSelectedCount = selectedRows.length;
    const visibleSelectedCount = modifiedRecordPaymentList.filter((invoice) => selectedRows.includes(invoice._id)).length;
    const hiddenSelectedCount = totalSelectedCount - visibleSelectedCount;

    return (
        <>
            <Box
                sx={{
                    bgcolor: 'background.main',
                    minHeight: 'calc(100vh - 64px)',
                    position: 'relative',
                    width: '100%',
                }}
            >
                <Box padding={'32px 24px'}>
                    {/* PageHeader */}
                    <RecordPaymentListHeader
                        showCustomerName={onlyOneCustomerSelected}
                        customerDetail={customerDetail}
                        // NEW: Pass selection counts for better UX
                        totalSelected={totalSelectedCount}
                        hiddenSelected={hiddenSelectedCount}
                    />

                    {/* Filters */}
                    <RecordPaymentListFilters
                        recordPaymentParams={recordPaymentParams}
                        setRecordPaymentParams={setRecordPaymentParams}
                        customerDetail={customerDetail}
                        setCustomerDetail={setCustomerDetail}
                    />

                    {/* Table with virtualization + infinite scroll */}
                    <RecordPaymentListTable
                        onlyOneCustomerSelected={onlyOneCustomerSelected}
                        recordPaymentList={modifiedRecordPaymentList}
                        recordPaymentPagaintion={recordPaymentPagaintion}
                        recordPaymentParams={recordPaymentParams}
                        setRecordPaymentParams={setRecordPaymentParams}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        getMultipleRecordPayments={getMultipleRecordPayments}
                        loadingMore={loadingMore}
                        updatePayment={updatePaymentInModifiedList}
                        addPayment={addPaymentToModifiedList}
                        deletePayment={deletePaymentFromModifiedList}
                        // NEW: Pass selection info for better UX
                        hiddenSelectedCount={hiddenSelectedCount}
                    />
                </Box>

                <RecordPaymentStickyBottom
                    onlyOneCustomerSelected={onlyOneCustomerSelected}
                    recordPaymentParams={recordPaymentParams}
                    selectedRows={selectedRows}
                    onDiscardChanges={handleDiscardChanges}
                    getTotalAdditionalPayment={getTotalAdditionalPayment}
                    onSave={handleSave}
                    onSingleExport={onSingleExport}
                    // NEW: Pass selection info for better UX
                    totalSelected={totalSelectedCount}
                    hiddenSelected={hiddenSelectedCount}
                />
            </Box>

            {onlyOneCustomerSelected && statementAccountModal && (
                <RecordPaymentSummaryModal
                    open={statementAccountModal}
                    close={setStatementAccountModal}
                    id={recordPaymentParams?.customer[0]}
                    data={customerDetail}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    getMultipleRecordPayments={getMultipleRecordPayments}
                />
            )}

            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RecordPaymentList;
